import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { CookieService } from 'ngx-cookie-service';
import { environment } from '../../../../../environments/environment';
import { ApiMethodService } from '../../../../tools/services/api/method-api/apiMethod.service';
import { ActionBackOfficeResponse } from '../../../models/api.model';
import {
  BankAccount,
  BankAccountPayload,
  SetBankAccountActivePayload,
} from '../../../models/finance/bank-account.model';

@Injectable({ providedIn: 'root' })
export class ApiBankAccountService {
  private readonly baseUrl = environment.apiPrivateUrl;
  private readonly cookieService = inject(CookieService);
  private readonly apiMethodService = inject(ApiMethodService);

  getBankAccountList(siteId: number, userId: number, includeInactive = true): Observable<BankAccount[]> {
    const token = this.cookieService.get('token');
    return this.apiMethodService
      .request<unknown>('/BankAccounts/getBankAccountList', 'GET', this.baseUrl, {
        isConsoleLog: false,
        labelLog: 'getBankAccountList',
        token,
        headerOnly: false,
        params: { siteId, userId, includeInactive },
      })
      .pipe(map((res) => (Array.isArray(res) ? res.map((row) => this.mapRow(row)) : [])));
  }

  insertBankAccount(payload: BankAccountPayload): Observable<ActionBackOfficeResponse> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<ActionBackOfficeResponse>(
      '/BankAccounts/insertBankAccount',
      'POST',
      this.baseUrl,
      { isConsoleLog: false, labelLog: 'insertBankAccount', token, headerOnly: false, body: payload }
    );
  }

  updateBankAccount(id: number, payload: BankAccountPayload): Observable<ActionBackOfficeResponse> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<ActionBackOfficeResponse>(
      `/BankAccounts/updateBankAccount/${id}`,
      'PATCH',
      this.baseUrl,
      { isConsoleLog: false, labelLog: 'updateBankAccount', token, headerOnly: false, body: payload }
    );
  }

  setBankAccountActive(id: number, payload: SetBankAccountActivePayload): Observable<ActionBackOfficeResponse> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<ActionBackOfficeResponse>(
      `/BankAccounts/setBankAccountActive/${id}`,
      'PATCH',
      this.baseUrl,
      { isConsoleLog: false, labelLog: 'setBankAccountActive', token, headerOnly: false, body: payload }
    );
  }

  private mapRow(raw: unknown): BankAccount {
    const r = raw as Record<string, unknown>;
    const read = (camel: string, pascal: string) => r[camel] ?? r[pascal];
    return {
      id: Number(read('id', 'Id') ?? 0),
      siteId: Number(read('siteId', 'SiteId') ?? 0),
      siteName: String(read('siteName', 'SiteName') ?? ''),
      name: String(read('name', 'Name') ?? ''),
      bankName: String(read('bankName', 'BankName') ?? ''),
      accountName: String(read('accountName', 'AccountName') ?? ''),
      accountNumber: String(read('accountNumber', 'AccountNumber') ?? ''),
      accountType: String(read('accountType', 'AccountType') ?? ''),
      active: Boolean(read('active', 'Active')),
      writeDate: (read('writeDate', 'WriteDate') ?? null) as string | null,
    };
  }
}
